/**
 * NotificationBell Component
 *
 * Bell icon with animated badge for unread count.
 * Opens NotificationPopover on click.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Bell } from "lucide-react";
import { useUnreadNotificationCount } from "@/hooks/use-notifications";
import { NotificationPopover } from "./NotificationPopover";
import { cn } from "@/lib/utils";

// ═══════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: unreadCount = 0, isLoading } = useUnreadNotificationCount();

  // Animate badge when count changes
  const [isAnimating, setIsAnimating] = useState(false);
  const prevCountRef = useRef(unreadCount);

  useEffect(() => {
    if (unreadCount > prevCountRef.current) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 600);
      return () => clearTimeout(timer);
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const displayCount = unreadCount > 99 ? "99+" : unreadCount;

  return (
    <div ref={containerRef} className="relative">
      {/* Bell Button */}
      <button
        onClick={handleToggle}
        className={cn(
          "relative p-2 rounded-lg transition-all duration-200",
          isOpen
            ? "bg-gray-100 text-gray-900"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        )}
        aria-label={`Notifikationer${unreadCount > 0 ? `, ${unreadCount} olästa` : ""}`}
        aria-expanded={isOpen}
      >
        <Bell
          className={cn(
            "w-5 h-5 transition-transform duration-200",
            isAnimating && "animate-wiggle"
          )}
        />

        {/* Badge */}
        {!isLoading && unreadCount > 0 && (
          <span
            className={cn(
              "absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full shadow-sm",
              "transition-all duration-300",
              isAnimating && "animate-bounce-once scale-125"
            )}
          >
            {displayCount}
          </span>
        )}

        {/* Pulse animation for new notifications */}
        {!isLoading && unreadCount > 0 && isAnimating && (
          <span className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] bg-red-500 rounded-full animate-ping opacity-75" />
        )}
      </button>

      {/* Popover */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div
            className="fixed inset-0 z-40 bg-black/20 sm:hidden"
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Popover container */}
          <div
            className={cn(
              "z-50 mt-2",
              // Mobile: fixed position, centered horizontally
              "fixed left-4 right-4 top-16",
              // Desktop: absolute position, right-aligned
              "sm:absolute sm:left-auto sm:top-auto sm:right-0"
            )}
          >
            <NotificationPopover onClose={handleClose} />
          </div>
        </>
      )}
    </div>
  );
}
